import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Award, AlertCircle, HelpCircle } from 'lucide-react';
import Card from './ui/Card';
import Button from './ui/Button';
import Alert from './ui/Alert';
import LoadingSpinner from './ui/LoadingSpinner';
import stateLicenseService from '../services/unifiedStateLicenseService';
import crowdsourcingService from '../services/crowdsourcingService';
import { getAllStatesWithLicensure, getStateLicenses, getAllStates, getStateStatus } from '../data/interpreterLicensureRequirements';
import { useAuth } from '../contexts/AuthContext';

export default function StateLicensesCard({ userId }) {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [showCrowdsourcing, setShowCrowdsourcing] = useState(false);

  useEffect(() => {
    if (userId) {
      loadStateLicenses();
    }
  }, [userId]);

  const loadStateLicenses = async () => {
    setLoading(true);
    try {
      const result = await stateLicenseService.getUserStateLicenses(userId);
      if (result.success) {
        setLicenses(result.licenses);
      } else {
        setError('Failed to load state licenses');
      }
    } catch (error) {
      setError('Failed to load state licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLicense = () => {
    setShowAddForm(true);
    setEditingLicense(null);
  };

  const handleEditLicense = (license) => {
    setEditingLicense(license);
    setShowAddForm(true);
  };

  const handleDeleteLicense = async (licenseId) => {
    if (!window.confirm('Are you sure you want to delete this state license?')) {
      return;
    }

    try {
      const result = await stateLicenseService.deleteUserStateLicense(licenseId, userId);
      if (result.success) {
        setLicenses(licenses.filter(l => l.licenseId !== licenseId));
        setError(''); // Clear any previous errors
      } else {
        setError(result.error || 'Failed to delete license');
      }
    } catch (error) {
      console.error('❌ Error deleting license:', error);
      setError('Failed to delete license');
    }
  };

  const handleSaveLicense = async (licenseData) => {
    try {
      setError(''); // Clear any previous errors
      let result;
      
      if (editingLicense) {
        // Update existing license - include userId in updates
        result = await stateLicenseService.updateUserStateLicense(
          editingLicense.licenseId, 
          { ...licenseData, userId }
        );
      } else {
        // Add new license
        result = await stateLicenseService.addUserStateLicense(userId, licenseData);
      }

      if (result.success) {
        setShowAddForm(false);
        setEditingLicense(null);
        await loadStateLicenses(); // Reload to get updated data
        
        // Show success message
        const action = editingLicense ? 'updated' : 'added';
        console.log(`✅ License ${action} successfully:`, result.licenseId || editingLicense?.licenseId);
      } else {
        setError(result.error || 'Failed to save license');
      }
    } catch (error) {
      console.error('❌ Error saving license:', error);
      setError('Failed to save license');
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="sm" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">State Licenses</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddLicense}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add License
        </Button>
      </div>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {licenses.length === 0 && !showAddForm ? (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No state licenses added yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Add your state interpreter licenses to track requirements and progress
          </p>
          <Button variant="primary" onClick={handleAddLicense}>
            Add Your First License
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {licenses.map((license) => (
            <div
              key={license.licenseId}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <h4 className="font-medium text-gray-900">
                  {license.state} - {license.licenseType}
                </h4>
                {license.licenseNumber && (
                  <p className="text-sm text-gray-600">
                    License #: {license.licenseNumber}
                  </p>
                )}
                {license.expirationDate && (
                  <p className="text-sm text-gray-600">
                    Expires: {new Date(license.expirationDate).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditLicense(license)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteLicense(license.licenseId)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddForm && (
        <StateLicenseForm
          license={editingLicense}
          user={user}
          onSave={handleSaveLicense}
          onCancel={() => {
            setShowAddForm(false);
            setEditingLicense(null);
          }}
          onShowCrowdsourcing={() => {
            setShowAddForm(false);
            setShowCrowdsourcing(true);
          }}
        />
      )}

      {showCrowdsourcing && (
        <CrowdsourcingForm
          user={user}
          onSave={async (data) => {
            try {
              const result = await crowdsourcingService.submitStateRequirements(data);
              if (result.success) {
                setShowCrowdsourcing(false);
                // Show success message
                alert('Thank you! Your submission has been received and will be reviewed by our team.');
              } else {
                setError(result.error || 'Failed to submit information');
              }
            } catch (error) {
              setError('Failed to submit information. Please try again.');
            }
          }}
          onCancel={() => setShowCrowdsourcing(false)}
        />
      )}
    </Card>
  );
}

// State License Form Component - Context-Aware for Existing Users
function StateLicenseForm({ license, user, onSave, onCancel, onShowCrowdsourcing }) {
  // Smart defaults based on user profile for existing users
  const getSmartDefaults = () => {
    if (license) {
      // Editing existing license
      return {
        state: license.state || '',
        licenseType: license.licenseType || '',
        licenseNumber: license.licenseNumber || '',
        issueDate: license.issueDate || '',
        expirationDate: license.expirationDate || '',
        status: license.status || 'active'
      };
    } else {
      // Adding new license - use smart defaults for existing users
      return {
        state: user?.state || '', // Prefill from user profile
        licenseType: '',
        licenseNumber: '',
        issueDate: '',
        expirationDate: '',
        status: 'active'
      };
    }
  };

  const [formData, setFormData] = useState(getSmartDefaults());

  const allStates = getAllStates();
  
  // Get state status and available license types
  const stateStatus = formData.state ? getStateStatus(formData.state) : null;
  const availableLicenseTypes = formData.state ? getStateLicenses(formData.state) : [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    // If changing state, reset license type
    if (field === 'state') {
      setFormData(prev => ({ ...prev, [field]: value, licenseType: '' }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">
          {license ? 'Edit State License' : 'Add State License'}
        </h4>
        {!license && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowCrowdsourcing}
            className="flex items-center gap-1 text-primary hover:text-primary-dark"
          >
            <HelpCircle className="w-4 h-4" />
            Correct or Add State Info
          </Button>
        )}
      </div>
      
      {/* Smart context message for existing users */}
      {!license && user?.state && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Smart suggestion:</span> We've prefilled your state as {user.state} based on your profile. 
            You can change this if you need to add a license from a different state.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Select a state</option>
              {allStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
              <option value="other">My state/territory is not listed</option>
            </select>
            
            {/* Show state status message */}
            {stateStatus && (
              <div className={`mt-2 p-2 rounded text-sm ${
                stateStatus.hasData 
                  ? (stateStatus.hasLicensure || stateStatus.hasRegistry)
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
                  : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
              }`}>
                <p>{stateStatus.message}</p>
                {!stateStatus.hasData && (
                  <p className="mt-1 text-xs">
                    <button 
                      type="button"
                      onClick={onShowCrowdsourcing}
                      className="text-primary hover:underline font-medium"
                    >
                      Help us add {formData.state} requirements →
                    </button>
                  </p>
                )}
                {stateStatus.hasData && !stateStatus.hasLicensure && !stateStatus.hasRegistry && (
                  <p className="mt-1 text-xs">
                    If this is incorrect, 
                    <button 
                      type="button"
                      onClick={onShowCrowdsourcing}
                      className="text-primary hover:underline font-medium ml-1"
                    >
                      let us know →
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Type *
            </label>
            {availableLicenseTypes.length > 0 ? (
              <select
                value={formData.licenseType}
                onChange={(e) => handleChange('licenseType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
                disabled={!formData.state}
              >
                <option value="">
                  {formData.state ? 'Select license type' : 'Select a state first'}
                </option>
                {availableLicenseTypes.map(licenseType => (
                  <option key={licenseType.id} value={licenseType.name}>
                    {licenseType.name}
                  </option>
                ))}
                <option value="custom">Other (specify below)</option>
              </select>
            ) : (
              <input
                type="text"
                value={formData.licenseType}
                onChange={(e) => handleChange('licenseType', e.target.value)}
                placeholder={formData.state ? "Enter license type" : "Select a state first"}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
                disabled={!formData.state}
              />
            )}
            
            {/* Custom license type input when "Other" is selected */}
            {formData.licenseType === 'custom' && (
              <input
                type="text"
                placeholder="Enter custom license type"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary mt-2"
                onChange={(e) => handleChange('licenseType', e.target.value)}
                required
              />
            )}
            
            {/* Show license requirements if available */}
            {formData.state && formData.licenseType && formData.licenseType !== 'custom' && (
              (() => {
                const selectedLicense = availableLicenseTypes.find(lt => lt.name === formData.licenseType);
                return selectedLicense ? (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                    <p className="font-medium text-blue-900">Requirements:</p>
                    <p className="text-blue-800">{selectedLicense.requirements}</p>
                    <p className="text-blue-700 mt-1">
                      <span className="font-medium">CEU Requirements:</span> {selectedLicense.ceuRequirements}
                    </p>
                    <p className="text-blue-700">
                      <span className="font-medium">Renewal:</span> {selectedLicense.renewalPeriod}
                    </p>
                    {selectedLicense.ceuRequirements.includes('crowdsourcing') && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-yellow-800 text-xs">
                          <span className="font-medium">Help needed:</span> We need community input on this state's requirements. 
                          <button 
                            type="button"
                            onClick={onShowCrowdsourcing}
                            className="text-primary hover:underline ml-1"
                          >
                            Click here to correct or add state info.
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                ) : null;
              })()
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Number
            </label>
            <input
              type="text"
              value={formData.licenseNumber}
              onChange={(e) => handleChange('licenseNumber', e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date
            </label>
            <input
              type="date"
              value={formData.expirationDate}
              onChange={(e) => handleChange('expirationDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" variant="primary">
            {license ? 'Update License' : 'Add License'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// Crowdsourcing Form Component - Context-Aware for User's Profession
function CrowdsourcingForm({ user, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    state: user?.state || '',
    licenseType: user?.profession ? 
      (user.profession === 'interpreter' ? 'ASL Interpreter License' : 
       user.profession === 'nurse' ? 'RN License' : 
       `${user.profession} License`) : '',
    ceuRequirements: '',
    renewalPeriod: '',
    fees: '',
    sourceUrl: '',
    notes: '',
    userEmail: user?.email || '',
    profession: user?.profession || 'interpreter'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const statesWithLicensure = getAllStatesWithLicensure();

  return (
    <div className="mt-4 p-4 border border-primary/20 bg-primary/5 rounded-lg">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-primary" />
        <h4 className="font-medium text-gray-900">
          Correct or Add {user?.profession === 'nurse' ? 'Nursing' : 'Interpreter'} Requirements
        </h4>
      </div>
      
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <span className="font-medium">Help us get it right!</span> If you notice incorrect information or we're missing your state's requirements, 
          please share the correct details. All submissions are reviewed and verified before updating our database.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              value={formData.state}
              onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Select a state</option>
              {getAllStates().map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
              <option value="other">My state/territory is not listed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profession Type *
            </label>
            {user?.profession ? (
              // Pre-populated for existing users
              <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-700">
                {user.profession === 'interpreter' ? 'ASL Interpreter' : 
                 user.profession === 'nurse' ? 'Registered Nurse' : 
                 user.profession.charAt(0).toUpperCase() + user.profession.slice(1)}
              </div>
            ) : (
              // Dropdown for new users
              <select
                value={formData.licenseType}
                onChange={(e) => setFormData(prev => ({ ...prev, licenseType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">Select profession type</option>
                <option value="ASL Interpreter License">ASL Interpreter</option>
                <option value="RN License">Registered Nurse</option>
                <option value="LPN License">Licensed Practical Nurse</option>
                <option value="Other Professional License">Other Healthcare Professional</option>
              </select>
            )}
            {user?.profession && (
              <p className="mt-1 text-xs text-gray-600">
                Pre-filled based on your profile. We're asking about {user.profession === 'interpreter' ? 'interpreter' : user.profession} licensing requirements in the selected state.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CEU Requirements *
            </label>
            <input
              type="text"
              value={formData.ceuRequirements}
              onChange={(e) => setFormData(prev => ({ ...prev, ceuRequirements: e.target.value }))}
              placeholder="e.g., 30 hours every 2 years, Follow RID requirements"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Renewal Period *
            </label>
            <input
              type="text"
              value={formData.renewalPeriod}
              onChange={(e) => setFormData(prev => ({ ...prev, renewalPeriod: e.target.value }))}
              placeholder="e.g., Every 2 years, Annual"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fees
            </label>
            <input
              type="text"
              value={formData.fees}
              onChange={(e) => setFormData(prev => ({ ...prev, fees: e.target.value }))}
              placeholder="e.g., $150 initial, $100 renewal"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Official Source URL *
            </label>
            <input
              type="url"
              value={formData.sourceUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, sourceUrl: e.target.value }))}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Any additional details about requirements, special conditions, etc."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" variant="primary">
            Submit Information
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}